import { useStrings } from "@1js/localization-components";
import { type Group } from "@1js/pl-types";
import type {
  ComboboxOpenChangeData,
  ComboboxOpenEvents,
} from "@fluentui/react-components";
import {
  Combobox,
  OptionGroup,
  Skeleton,
  SkeletonItem,
  makeStyles,
} from "@fluentui/react-components";
import React, { useCallback } from "react";
import { GroupOption } from "../GroupOption/GroupOption";
import type { Appearance } from "../data";
import {
  FilterLabel,
  MemberTeamsLabel,
  OwnerTeamsLabel,
} from "./GroupPromptsFilter.strings";

export type GroupPromptsFiltersProps = {
  isLoading?: boolean;
  appearance?: Appearance;
  value?: GroupPromptsFilters;
  onFilterChanged?: (filter: GroupPromptsFilters) => void;
  groups: Group[];
  isStoreStateEnabled: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export type GroupPromptsFilters = "Default" | string;

const useStyles = makeStyles({
  dropdown: {
    minWidth: "180px",
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const checkIsGroup = (group: any): group is Group =>
  group && Object.keys(group).some((name) => name === "IsMember");

export const GroupPromptsFilterStateId = "WorkGroupID";

export const GroupPromptsFilter: React.FC<GroupPromptsFiltersProps> =
  React.memo(
    ({
      isLoading,
      value,
      onFilterChanged,
      groups,
      isStoreStateEnabled,
      onOpenChange,
    }) => {
      value = value || "Default";
      const localizations = useStrings({
        FilterLabel,
        OwnerTeamsLabel,
        MemberTeamsLabel,
      });

      const handleOnOpenChange = React.useCallback(
        (_: ComboboxOpenEvents, data: ComboboxOpenChangeData) => {
          onOpenChange && onOpenChange(data.open);
        },
        [onOpenChange]
      );

      const options: Record<GroupPromptsFilters, Group> = React.useMemo(() => {
        const newOptions: Record<GroupPromptsFilters, Group> = {};
        groups
          .filter((group) => group.IsMember || group.IsOwner)
          .forEach((group) => {
            newOptions[group.Id] = group;
          });

        return newOptions;
      }, [groups]);

      if (!options[value]) {
        // it can happen if the user was just removed from the group, but the old value is still present in local storage
        value = "Default";
      }

      const defaultStyles = useStyles();

      const handleOnOptionSelect = useCallback(
        (value: GroupPromptsFilters) => {
          onFilterChanged && onFilterChanged(value);
          if (isStoreStateEnabled) {
            localStorage.setItem(GroupPromptsFilterStateId, value);
          }
        },
        [isStoreStateEnabled, onFilterChanged]
      );

      if (groups.length === 0) {
        return <></>;
      }

      const ownerOptions = Object.entries(options).filter(
        ([_, group]) => checkIsGroup(group) && group.IsOwner
      );
      const memberOptions = Object.entries(options).filter(
        ([_, group]) => checkIsGroup(group) && !group.IsOwner && group.IsMember
      );

      return isLoading ? (
        <Skeleton>
          <SkeletonItem size={32} style={{ width: "160px" }} />
        </Skeleton>
      ) : (
        <Combobox
          placeholder={localizations.FilterLabel()}
          onOptionSelect={(_, data) =>
            handleOnOptionSelect(data.optionValue as GroupPromptsFilters)
          }
          multiselect={false}
          selectedOptions={[value]}
          value={
            (value !== "Default" && options[value].DisplayName) || undefined
          }
          className={defaultStyles.dropdown}
          onOpenChange={handleOnOpenChange}
          data-testid="grouppromptsfilter"
        >
          {ownerOptions.length !== 0 && (
            <OptionGroup label={localizations.OwnerTeamsLabel()}>
              {ownerOptions.map(([value]) => (
                <GroupOption key={value} group={options[value]} />
              ))}
            </OptionGroup>
          )}
          {memberOptions.length !== 0 && (
            <OptionGroup label={localizations.MemberTeamsLabel()}>
              {memberOptions.map(([value]) => (
                <GroupOption key={value} group={options[value]} />
              ))}
            </OptionGroup>
          )}
        </Combobox>
      );
    }
  );

GroupPromptsFilter.displayName = "GroupPromptsFilters";
